/**
 * Barefoot Sadelguide quiz UI — flow, results, live product data, Klaviyo event.
 */
(function () {
  'use strict';

  const STEPS = [
    'intro',
    'disciplin',
    'saede',
    'knaestoetter',
    'rytter',
    'hest',
    'vegansk',
    'email',
    'resultat',
  ];

  const QUESTION_STEPS = STEPS.filter((s) => !['intro', 'resultat'].includes(s));

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: currency || 'DKK',
        maximumFractionDigits: 0,
      }).format((cents || 0) / 100);
    } catch (_) {
      return `${Math.round((cents || 0) / 100)} kr.`;
    }
  }

  function whyText(score, svar) {
    const bits = [];
    if (score.er_vegansk && svar.vegansk_vigtigt) {
      bits.push('den er 100% vegansk (VegiTex)');
    }
    if (score.detaljer.saede > 0) {
      const map = { aabent: 'åbne sæde', medium: 'medium sæde', dybt: 'dybe, støttende sæde' };
      bits.push(`den har det ${map[svar.saede_oenske] || 'sæde'} du ønskede`);
    }
    if (score.detaljer.knaestoetter > 0) {
      bits.push(
        svar.knaestoetter_oenske === 'ja'
          ? 'du får knæstøtter til mere støtte'
          : 'du får frihed uden knæstøtter'
      );
    }
    if (score.detaljer.disciplin > 0) {
      bits.push('den passer til din disciplin');
    }
    if (score.maalstoerrelse != null) {
      bits.push(`størrelse ${score.maalstoerrelse} matcher dig`);
    }
    if (!bits.length) return 'Den matcher dig godt ud fra dine svar.';
    return `Denne passer dig, fordi ${bits.slice(0, 3).join(', ')}.`;
  }

  function findVariant(product, score, mapEntry) {
    if (!product || !product.variants) return null;
    const size = score.maalstoerrelse;
    const filters = { ...(mapEntry.optionFilters || {}) };
    if (size != null) {
      filters.Størrelser = String(size);
      filters.Size = String(size);
    }

    const variants = product.variants;
    const match = variants.find((v) => {
      const opts = v.options || [];
      // Shopify ajax API: options is array of values in option order
      const named = {};
      (product.options || []).forEach((optName, i) => {
        named[optName] = opts[i];
      });
      for (const [key, val] of Object.entries(filters)) {
        if (!(key in named)) continue;
        if (String(named[key]).toLowerCase() !== String(val).toLowerCase()) return false;
      }
      // If size filter exists as option, require match when size known
      if (size != null) {
        const sizeOpt = Object.entries(named).find(([k]) => /størrelse|size/i.test(k));
        if (sizeOpt && !String(sizeOpt[1]).includes(String(size))) return false;
      }
      if (mapEntry.optionFilters) {
        for (const [key, val] of Object.entries(mapEntry.optionFilters)) {
          if (String(named[key] || '').toLowerCase() !== String(val).toLowerCase()) return false;
        }
      }
      return true;
    });
    return match || variants.find((v) => v.available) || variants[0] || null;
  }

  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`Produkt ikke fundet: ${handle}`);
    return res.json();
  }

  function trackKlaviyo(publicKey, email, properties) {
    if (!email) return Promise.resolve({ ok: false, reason: 'no-email' });

    // Prefer onboarded Klaviyo object / _learnq when app is installed
    try {
      window._learnq = window._learnq || [];
      window._learnq.push([
        'identify',
        {
          $email: email,
          ...properties,
        },
      ]);
      window._learnq.push(['track', 'Sadelguide Completed', properties]);
    } catch (_) {
      /* ignore */
    }

    if (!publicKey) return Promise.resolve({ ok: true, via: 'learnq-only' });

    // Client API fallback (public key)
    const body = {
      data: {
        type: 'event',
        attributes: {
          properties,
          metric: { data: { type: 'metric', attributes: { name: 'Sadelguide Completed' } } },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties,
              },
            },
          },
        },
      },
    };

    return fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(publicKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15',
      },
      body: JSON.stringify(body),
    })
      .then((r) => ({ ok: r.ok || r.status === 202, via: 'client-api', status: r.status }))
      .catch((err) => ({ ok: false, reason: String(err) }));
  }

  class SadelguideQuiz {
    constructor(root) {
      this.root = root;
      this.pageContentHtml =
        root.closest('.shopify-section')?.querySelector('[data-sadelguide-page-content]')?.innerHTML ||
        root.parentElement?.querySelector('[data-sadelguide-page-content]')?.innerHTML ||
        '';
      this.step = 'intro';
      this.answers = {
        disciplin: null,
        saede_oenske: null,
        knaestoetter_oenske: null,
        koen: null,
        hoejde_cm: null,
        bukse_stoerrelse: null,
        hest_rygform: null,
        hest_type: null,
        vegansk_vigtigt: null,
        email: '',
        name: '',
      };
      this.sadler = null;
      this.productMap = null;
      this.config = {
        catalogUrl: root.dataset.catalogUrl,
        mapUrl: root.dataset.mapUrl,
        klaviyoPublicKey: root.dataset.klaviyoPublicKey || '',
        moneyFormat: root.dataset.moneyFormat || '',
      };
      this.cache = {};
      this.bind();
      this.render();
      this.preload();
    }

    bind() {
      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        if (action === 'start') this.go('disciplin');
        if (action === 'back') this.back();
        if (action === 'next') this.nextFromFields();
        if (action === 'skip-hest') {
          this.answers.hest_rygform = null;
          this.answers.hest_type = null;
          this.go('vegansk');
        }
        if (action === 'choose') {
          const field = btn.dataset.field;
          let value = btn.dataset.value;
          if (field === 'vegansk_vigtigt') value = value === 'true';
          this.answers[field] = value;
          this.root.querySelectorAll(`[data-field="${field}"]`).forEach((el) => {
            el.classList.toggle('is-selected', el === btn);
          });
          // Auto-advance for simple choice steps
          if (['disciplin', 'saede', 'knaestoetter', 'vegansk'].includes(this.step)) {
            window.setTimeout(() => this.next(), 180);
          }
        }
        if (action === 'submit-email') this.submitEmail();
        if (action === 'restart') this.restart();
      });
    }

    async preload() {
      try {
        const [sadler, map] = await Promise.all([
          fetch(this.config.catalogUrl).then((r) => r.json()),
          fetch(this.config.mapUrl).then((r) => r.json()),
        ]);
        this.sadler = sadler;
        this.productMap = map;
      } catch (err) {
        console.error('[sadelguide] preload failed', err);
        this.error = 'Guiden kunne ikke indlæses. Genindlæs siden, eller prøv igen senere.';
        this.render();
      }
    }

    go(step) {
      this.step = step;
      this.render();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    back() {
      const idx = STEPS.indexOf(this.step);
      if (idx > 0) this.go(STEPS[idx - 1]);
    }

    next() {
      const idx = STEPS.indexOf(this.step);
      if (idx < STEPS.length - 1) this.go(STEPS[idx + 1]);
    }

    nextFromFields() {
      if (this.step === 'rytter') {
        const koen = this.root.querySelector('[name="sg-koen"]:checked')?.value;
        const hoejde = parseFloat(this.root.querySelector('[name="sg-hoejde"]')?.value || '');
        const bukseRaw = this.root.querySelector('[name="sg-bukse"]')?.value;
        const bukse = bukseRaw ? parseInt(bukseRaw, 10) : null;
        if (!koen || !hoejde || Number.isNaN(hoejde)) {
          this.error = 'Vælg køn og angiv din højde.';
          this.render();
          return;
        }
        this.answers.koen = koen;
        this.answers.hoejde_cm = hoejde;
        this.answers.bukse_stoerrelse =
          bukse || window.SadelguideScoring.estimerBukseStoerrelse(koen, hoejde);
        this.error = null;
        this.go('hest');
        return;
      }
      if (this.step === 'hest') {
        this.answers.hest_type = this.root.querySelector('[name="sg-hest-type"]')?.value || null;
        this.answers.hest_rygform = this.root.querySelector('[name="sg-hest-ryg"]')?.value || null;
        if (!this.answers.hest_type) this.answers.hest_type = null;
        if (!this.answers.hest_rygform) this.answers.hest_rygform = null;
        this.go('vegansk');
      }
    }

    restart() {
      this.answers = {
        disciplin: null,
        saede_oenske: null,
        knaestoetter_oenske: null,
        koen: null,
        hoejde_cm: null,
        bukse_stoerrelse: null,
        hest_rygform: null,
        hest_type: null,
        vegansk_vigtigt: null,
        email: '',
        name: '',
      };
      this.result = null;
      this.error = null;
      this.go('intro');
    }

    progressIndex() {
      const i = QUESTION_STEPS.indexOf(this.step);
      return i < 0 ? (this.step === 'resultat' ? QUESTION_STEPS.length : 0) : i;
    }

    async submitEmail() {
      const email = (this.root.querySelector('[name="sg-email"]')?.value || '').trim();
      const name = (this.root.querySelector('[name="sg-name"]')?.value || '').trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.error = 'Skriv en gyldig e-mail, så vi kan sende anbefalingen.';
        this.render();
        return;
      }
      this.answers.email = email;
      this.answers.name = name;
      this.error = null;
      this.loading = true;
      this.go('resultat');
      await this.computeAndRenderResults();
    }

    buildSvar() {
      return {
        disciplin: this.answers.disciplin,
        saede_oenske: this.answers.saede_oenske,
        knaestoetter_oenske: this.answers.knaestoetter_oenske,
        koen: this.answers.koen,
        hoejde_cm: this.answers.hoejde_cm,
        bukse_stoerrelse: this.answers.bukse_stoerrelse,
        hest_rygform: this.answers.hest_rygform || null,
        hest_type: this.answers.hest_type || null,
        vegansk_vigtigt: this.answers.vegansk_vigtigt === true,
      };
    }

    async enrichScore(score) {
      if (!score) return null;
      const mapEntry = this.productMap[score.model] || {};
      const handle = mapEntry.handle;
      let product = null;
      let variant = null;
      if (handle) {
        try {
          if (!this.cache[handle]) this.cache[handle] = await fetchProduct(handle);
          product = this.cache[handle];
          variant = findVariant(product, score, mapEntry);
        } catch (err) {
          console.warn('[sadelguide] product fetch', handle, err);
        }
      }
      return {
        score,
        mapEntry,
        product,
        variant,
        why: whyText(score, this.buildSvar()),
      };
    }

    async computeAndRenderResults() {
      try {
        if (!this.sadler || !this.productMap || !window.SadelguideScoring) {
          throw new Error('Data mangler');
        }
        const svar = this.buildSvar();
        const match = window.SadelguideScoring.findBedsteMatch(this.sadler, svar);
        const bedste = await Promise.all(match.bedste_match.map((s) => this.enrichScore(s)));
        const alternativ = await this.enrichScore(match.alternativ);
        const prisvenligt = await this.enrichScore(match.prisvenligt_valg);
        const naest = await this.enrichScore(match.naestbedste_ikke_veganske_match);
        this.result = { bedste, alternativ, prisvenligt, naest, svar, match };

        const anbefalet = match.bedste_match.map((m) => m.model).join(', ');
        const props = {
          hesteryg_type: svar.hest_rygform || '',
          hest_type: svar.hest_type || '',
          disciplin: svar.disciplin,
          oensket_saede: svar.saede_oenske,
          knaestoetter_oensket: svar.knaestoetter_oenske,
          vegansk_vigtigt: svar.vegansk_vigtigt,
          anbefalet_model: anbefalet,
          anbefalet_alternativ: match.alternativ?.model || '',
          anbefalet_prisvenligt: match.prisvenligt_valg?.model || '',
          anbefalet_stoerrelse: match.bedste_match[0]?.maalstoerrelse ?? '',
          koen: svar.koen,
          hoejde_cm: svar.hoejde_cm,
          $first_name: this.answers.name || undefined,
        };
        trackKlaviyo(this.config.klaviyoPublicKey, this.answers.email, props);
      } catch (err) {
        console.error(err);
        this.error = 'Noget gik galt under beregningen. Prøv igen.';
      } finally {
        this.loading = false;
        this.render();
      }
    }

    optionButtons(field, options) {
      return options
        .map(([value, label, hint]) => {
          let selected = this.answers[field] === value;
          if (field === 'vegansk_vigtigt') {
            selected =
              this.answers.vegansk_vigtigt !== null &&
              String(!!this.answers.vegansk_vigtigt) === String(value);
          }
          return `
        <button type="button" class="sadelguide__option${selected ? ' is-selected' : ''}" data-action="choose" data-field="${field}" data-value="${value}">
          ${label}${hint ? `<small>${hint}</small>` : ''}
        </button>`;
        })
        .join('');
    }

    resultCard(item, badge, primary) {
      if (!item) return '';
      const { score, mapEntry, product, variant, why } = item;
      const title = mapEntry.label || score.model;
      const img =
        (variant && product?.images && product.featured_image) ||
        product?.featured_image ||
        (product?.images && product.images[0]) ||
        null;
      const imgUrl = typeof img === 'string' ? img : img?.src || null;
      const price = variant ? money(variant.price, product?.currency || 'DKK') : null;
      const available = variant ? !!variant.available : product ? product.available : null;
      const href = product
        ? `/products/${product.handle}${variant ? `?variant=${variant.id}` : ''}`
        : '#';
      const special = mapEntry.specialOrder
        ? `<p class="sadelguide__note">${mapEntry.deliveryNote || 'Specialbestilling'}</p>`
        : '';
      const vegitex = mapEntry.vegitexDonation
        ? `<p class="sadelguide__note">Ved køb af VegiTex doneres 5 kr. til Dyrenes Beskyttelse.</p>`
        : '';
      const variants =
        score.varianter && score.varianter.length
          ? `<p class="sadelguide__variants">Også som: ${score.varianter
              .map((v) => v.model)
              .join(', ')}</p>`
          : '';
      const size =
        score.maalstoerrelse != null
          ? `<span>Str. ${score.maalstoerrelse}</span>`
          : '';
      let stockHtml = '';
      if (available === true) stockHtml = `<span class="sadelguide__stock is-available">På lager</span>`;
      else if (available === false)
        stockHtml = `<span class="sadelguide__stock is-oos">Midlertidigt udsolgt / bestilles hjem</span>`;

      return `
        <article class="sadelguide__result-card${primary ? ' sadelguide__result-card--primary' : ''}">
          <div class="sadelguide__result-media">
            ${
              imgUrl
                ? `<img src="${imgUrl}" alt="${title}" width="280" height="280" loading="lazy">`
                : ''
            }
          </div>
          <div>
            <div class="sadelguide__badge">${badge}</div>
            <h3 class="sadelguide__result-title">${title}</h3>
            <p class="sadelguide__result-why">${why}</p>
            ${variants}
            ${special}
            ${vegitex}
            <div class="sadelguide__meta">
              ${price ? `<span class="sadelguide__price">${price}</span>` : ''}
              ${size}
              ${stockHtml}
            </div>
            <a class="sadelguide__btn sadelguide__btn--primary" href="${href}">Se sadlen</a>
          </div>
        </article>`;
    }

    render() {
      const qIndex = this.progressIndex();
      const qTotal = QUESTION_STEPS.length;
      const pct = this.step === 'intro' ? 0 : Math.round(((this.step === 'resultat' ? qTotal : qIndex + 1) / qTotal) * 100);
      const showProgress = this.step !== 'intro';

      let panel = '';
      if (this.error && this.step !== 'resultat') {
        panel += `<div class="sadelguide__error" role="alert">${this.error}</div>`;
      }

      if (this.step === 'intro') {
        panel += `
          <div class="sadelguide__panel">
            <p class="sadelguide__eyebrow">Personlig vejledning</p>
            <h2 class="sadelguide__title">Find din Barefoot-sadel</h2>
            <p class="sadelguide__lead">
              Svar på nogle få spørgsmål — så matcher vi dig med den Barefoot-sadel,
              der passer dig og din hest bedst. Du får resultatet med det samme og på e-mail.
            </p>
            ${this.pageContentHtml || ''}
            <div class="sadelguide__intro-cta">
              <button type="button" class="sadelguide__btn sadelguide__btn--primary" data-action="start">
                Start guiden
              </button>
            </div>
          </div>`;
      }

      if (this.step === 'disciplin') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Hvad rider du primært?</h2>
            <p class="sadelguide__hint">Vælg den disciplin, der fylder mest i din hverdag.</p>
            <div class="sadelguide__options">
              ${this.optionButtons('disciplin', [
                ['dressur', 'Dressur'],
                ['western', 'Western'],
                ['tur_distance', 'Tur / distance'],
                ['allround', 'Allround'],
                ['rideskole', 'Rideskole', 'Vi prioriterer robuste, delbare sadler'],
              ])}
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
            </div>
          </div>`;
      }

      if (this.step === 'saede') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Hvordan vil du gerne sidde?</h2>
            <p class="sadelguide__hint">Tænk på støtten i sædet — ikke på mærket.</p>
            <div class="sadelguide__options">
              ${this.optionButtons('saede_oenske', [
                ['aabent', 'Frit / åbent', 'Mest frihed i sædet'],
                ['medium', 'Medium', 'Balance mellem frihed og støtte'],
                ['dybt', 'Dybt / støttende', 'Mere oprejst og holdt'],
              ])}
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
            </div>
          </div>`;
      }

      if (this.step === 'knaestoetter') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Ønsker du knæstøtter?</h2>
            <p class="sadelguide__hint">Knæstøtter giver mere støtte — uden dem sidder du tættere og friere.</p>
            <div class="sadelguide__options">
              ${this.optionButtons('knaestoetter_oenske', [
                ['ja', 'Ja, gerne knæstøtter'],
                ['nej', 'Nej, uden knæstøtter'],
                ['ligegyldigt', 'Det er ligegyldigt'],
              ])}
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
            </div>
          </div>`;
      }

      if (this.step === 'rytter') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Fortæl lidt om dig</h2>
            <p class="sadelguide__hint">Vi bruger højde (og evt. buksestørrelse) til at finde den rigtige sadelstørrelse.</p>
            <div class="sadelguide__fields">
              <div class="sadelguide__field">
                <label>Køn</label>
                <div class="sadelguide__gender">
                  <label class="sadelguide__option"><input type="radio" name="sg-koen" value="kvinde" ${
                    this.answers.koen === 'kvinde' ? 'checked' : ''
                  }> Kvinde</label>
                  <label class="sadelguide__option"><input type="radio" name="sg-koen" value="mand" ${
                    this.answers.koen === 'mand' ? 'checked' : ''
                  }> Mand</label>
                </div>
              </div>
              <div class="sadelguide__field">
                <label for="sg-hoejde">Højde (cm)</label>
                <input id="sg-hoejde" name="sg-hoejde" type="number" min="140" max="210" step="1" inputmode="numeric" value="${
                  this.answers.hoejde_cm || ''
                }" placeholder="fx 168">
              </div>
              <div class="sadelguide__field">
                <label for="sg-bukse">Ca. buksestørrelse (valgfrit)</label>
                <input id="sg-bukse" name="sg-bukse" type="number" min="30" max="60" step="1" inputmode="numeric" value="${
                  this.answers.bukse_stoerrelse || ''
                }" placeholder="fx 38">
              </div>
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
              <button type="button" class="sadelguide__btn sadelguide__btn--primary" data-action="next">Fortsæt</button>
            </div>
          </div>`;
      }

      if (this.step === 'hest') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Din hest (valgfrit)</h2>
            <p class="sadelguide__hint">Hvis du kender type eller rygform, kan det finpudse anbefalingen. Ellers spring over.</p>
            <div class="sadelguide__fields">
              <div class="sadelguide__field">
                <label for="sg-hest-type">Hestetype</label>
                <select id="sg-hest-type" name="sg-hest-type">
                  <option value="">Ved ikke / spring over</option>
                  <option value="islaender">Islænder</option>
                  <option value="tinker">Tinker</option>
                  <option value="pony_lille_hest">Pony / lille hest</option>
                  <option value="cob">Cob</option>
                  <option value="irish_cob">Irish cob</option>
                  <option value="draft_hest">Draft / koldblod</option>
                  <option value="stor_hest">Stor hest</option>
                </select>
              </div>
              <div class="sadelguide__field">
                <label for="sg-hest-ryg">Rygform</label>
                <select id="sg-hest-ryg" name="sg-hest-ryg">
                  <option value="">Ved ikke / spring over</option>
                  <option value="kort_ryg">Kort ryg</option>
                  <option value="lang_ryg">Lang ryg</option>
                  <option value="hoejt_mankeben">Højt mankeben</option>
                  <option value="bred_lille_manke">Bred hest / lille manke</option>
                  <option value="skraa_ryg_bagud">Skrå ryg bagud</option>
                  <option value="spinkel_bygning">Spinkel bygning</option>
                </select>
              </div>
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
              <button type="button" class="sadelguide__btn sadelguide__btn--secondary" data-action="skip-hest">Spring over</button>
              <button type="button" class="sadelguide__btn sadelguide__btn--primary" data-action="next">Fortsæt</button>
            </div>
          </div>`;
      }

      if (this.step === 'vegansk') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Er vegansk materiale vigtigt for dig?</h2>
            <p class="sadelguide__hint">Vi kan prioritere VegiTex-modeller — og stadig vise et godt læderalternativ.</p>
            <div class="sadelguide__options">
              ${this.optionButtons('vegansk_vigtigt', [
                ['true', 'Ja, gerne vegansk'],
                ['false', 'Nej, det er ikke afgørende'],
              ])}
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
            </div>
          </div>`;
      }

      if (this.step === 'email') {
        panel += `
          <div class="sadelguide__panel">
            <h2 class="sadelguide__question">Hvor skal vi sende anbefalingen?</h2>
            <p class="sadelguide__hint">Du ser resultatet med det samme — og får det også på e-mail, så du kan læse i ro og mag.</p>
            <div class="sadelguide__fields">
              <div class="sadelguide__field">
                <label for="sg-name">Navn (valgfrit)</label>
                <input id="sg-name" name="sg-name" type="text" autocomplete="name" value="${
                  this.answers.name || ''
                }">
              </div>
              <div class="sadelguide__field">
                <label for="sg-email">E-mail</label>
                <input id="sg-email" name="sg-email" type="email" autocomplete="email" required value="${
                  this.answers.email || ''
                }" placeholder="dig@email.dk">
              </div>
            </div>
            <div class="sadelguide__actions">
              <button type="button" class="sadelguide__btn sadelguide__btn--ghost" data-action="back">Tilbage</button>
              <button type="button" class="sadelguide__btn sadelguide__btn--primary" data-action="submit-email">Se min anbefaling</button>
            </div>
          </div>`;
      }

      if (this.step === 'resultat') {
        if (this.loading || !this.result) {
          panel += `<div class="sadelguide__panel"><p class="sadelguide__loading">Finder den bedste match til dig…</p></div>`;
        } else if (this.error) {
          panel += `<div class="sadelguide__panel"><div class="sadelguide__error">${this.error}</div>
            <button type="button" class="sadelguide__btn sadelguide__btn--primary" data-action="restart">Prøv igen</button></div>`;
        } else {
          const { bedste, alternativ, prisvenligt, naest } = this.result;
          panel += `
            <div class="sadelguide__panel">
              <p class="sadelguide__eyebrow">Dit resultat</p>
              <h2 class="sadelguide__title">Her er vores anbefaling</h2>
              <p class="sadelguide__lead">Vi har også sendt den til ${this.answers.email}.</p>
              <div class="sadelguide__results" style="margin-top:1.25rem">
                ${bedste.map((item, i) => this.resultCard(item, bedste.length > 1 ? `Bedste match ${i + 1}` : 'Bedste match', true)).join('')}
                ${this.resultCard(alternativ, 'Godt alternativ', false)}
                ${this.resultCard(prisvenligt, 'Prisvenligt valg', false)}
                ${naest ? this.resultCard(naest, 'Næstbedste (ikke-vegansk)', false) : ''}
              </div>
              <div class="sadelguide__actions sadelguide__restart">
                <button type="button" class="sadelguide__btn sadelguide__btn--secondary" data-action="restart">Start forfra</button>
              </div>
            </div>`;
        }
      }

      this.root.innerHTML = `
        <div class="sadelguide__inner">
          ${
            showProgress
              ? `<div class="sadelguide__progress" aria-hidden="true">
                  <div class="sadelguide__progress-meta">
                    <span>${this.step === 'resultat' ? 'Færdig' : `Spørgsmål ${Math.min(qIndex + 1, qTotal)} af ${qTotal}`}</span>
                    <span>${pct}%</span>
                  </div>
                  <div class="sadelguide__progress-track"><div class="sadelguide__progress-fill" style="width:${pct}%"></div></div>
                </div>`
              : ''
          }
          ${panel}
        </div>`;
    }
  }

  function init() {
    document.querySelectorAll('[data-sadelguide]').forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = 'true';
      new SadelguideQuiz(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
